import "antd/dist/antd.css";
import "../App.css";

import { useMemo } from "react";
import { Table } from "antd";
import { useLoaderData } from "react-router-dom";
import GuideArticle from "../components/GuideArticle";
import GuideSection from "../components/GuideSection";
import GuideStackLead from "../components/GuideStackLead";
import GuideCtaBand from "../components/GuideCtaBand";
import RelatedGuides from "../components/RelatedGuides";
import usePageMeta from "../usePageMeta";
import { apiFetch } from "../apiFetch";
import { GUIDES } from "../data/guides";

const META = GUIDES.find((g) => g.slug === "fuel-prices")!;

type FuelRow = {
    countryCode: string;
    country: string;
    flag?: string;
    currency: string;
    petrol95: number | null;     // €/L
    diesel: number | null;       // €/L
    lpg: number | null;          // €/L
    localPetrol95?: number | null;
    localDiesel?: number | null;
    weekOf?: string;             // ISO date of the week the bulletin covers
    updatedAt?: string;          // ISO timestamp of the last refresh
};

type LoaderData = {
    rows: FuelRow[];
    weekOf: string | null;
    updatedAt: string | null;
};

export const fuelPricesLoader = async (): Promise<LoaderData> => {
    // The prerender step runs this loader against build/ served from
    // localhost — it can't reach the real API. Any network failure has
    // to fall back to an empty payload so React Router still renders
    // the page (with a "data isn't available right now" state) instead
    // of routing to errorElement, which would leave puppeteer waiting
    // for a header that never mounts.
    let response;
    try {
        response = await apiFetch(`/fuel-prices`);
    } catch {
        return { rows: [], weekOf: null, updatedAt: null };
    }
    if (!response.ok) {
        return { rows: [], weekOf: null, updatedAt: null };
    }
    let data;
    try {
        data = await response.json();
    } catch {
        return { rows: [], weekOf: null, updatedAt: null };
    }
    const rows = (data.fuelPrices || []) as FuelRow[];
    const weekOf = rows.reduce<string | null>(
        (latest, r) => (r.weekOf && (!latest || r.weekOf > latest) ? r.weekOf : latest),
        null,
    );
    const updatedAt = rows.reduce<string | null>(
        (latest, r) => (r.updatedAt && (!latest || r.updatedAt > latest) ? r.updatedAt : latest),
        null,
    );
    return { rows, weekOf, updatedAt };
};

const formatEur = (v: number | null | undefined) =>
    typeof v === "number" ? `€${v.toFixed(3)}` : "—";

const formatLocal = (v: number | null | undefined, currency: string) =>
    typeof v === "number" ? `${v.toFixed(3)} ${currency}` : "—";

const formatDate = (iso?: string | null) => {
    if (!iso) return "—";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

function FuelPrices() {
    const { rows, weekOf, updatedAt } = useLoaderData() as LoaderData;

    usePageMeta({
        title: "Fuel prices by country — petrol & diesel | Planamoto",
        description:
            "Average pump prices for unleaded 95 and diesel across the EU, UK and US. Refreshed weekly from the EU Commission Weekly Oil Bulletin, gov.uk and the U.S. EIA. Useful for cross-border tour planning and budgeting.",
        url: "https://www.planamoto.com/guides/fuel-prices",
    });

    // Cheapest-to-dearest on Petrol 95 gives the "fill up here" reading the
    // redesign leads with; rows missing a price sort to the end.
    const sorted = useMemo(
        () =>
            [...rows].sort((a, b) => {
                if (a.petrol95 == null && b.petrol95 == null) return 0;
                if (a.petrol95 == null) return 1;
                if (b.petrol95 == null) return -1;
                return a.petrol95 - b.petrol95;
            }),
        [rows],
    );

    // Numeric sort helper — treats a missing price as +∞ so blank cells sort
    // to the bottom when ordering cheapest-first (the common case).
    const num = (v: number | null | undefined) =>
        v == null ? Number.POSITIVE_INFINITY : v;

    const columns = [
        {
            title: "Country",
            dataIndex: "country",
            key: "country",
            sorter: (a: FuelRow, b: FuelRow) => a.country.localeCompare(b.country),
            render: (_: any, row: FuelRow) =>
                `${row.flag ? row.flag + " " : ""}${row.country}`,
        },
        {
            title: "Petrol 95 €/L",
            key: "petrol",
            align: "right" as const,
            sorter: (a: FuelRow, b: FuelRow) => num(a.petrol95) - num(b.petrol95),
            render: (_: any, row: FuelRow) => formatEur(row.petrol95),
        },
        {
            title: "Diesel €/L",
            key: "diesel",
            align: "right" as const,
            sorter: (a: FuelRow, b: FuelRow) => num(a.diesel) - num(b.diesel),
            render: (_: any, row: FuelRow) => formatEur(row.diesel),
        },
        {
            title: "LPG €/L",
            key: "lpg",
            align: "right" as const,
            sorter: (a: FuelRow, b: FuelRow) => num(a.lpg) - num(b.lpg),
            render: (_: any, row: FuelRow) => formatEur(row.lpg),
        },
        {
            title: "Local petrol",
            key: "localPetrol",
            align: "right" as const,
            sorter: (a: FuelRow, b: FuelRow) =>
                num(a.localPetrol95) - num(b.localPetrol95),
            render: (_: any, row: FuelRow) =>
                formatLocal(row.localPetrol95, row.currency),
        },
    ];

    return (
        <GuideArticle
            eyebrow="Reference · updated weekly"
            title="Fuel prices by country"
            lede="Average pump prices across the EU, the UK and the US. Useful for deciding where to fill up before a border crossing — the gap between cheapest and dearest in Europe is normally around 50–60 cents per litre. Compiled from three weekly sources with currency conversion from the live exchange rate feed."
            heroImage={{
                src: META.image!.src,
                alt: META.image!.alt ?? "Fuel pump nozzle at a service-station forecourt",
            }}
            beforeSections={
                <>
                    <GuideStackLead
                        eyebrow="How I actually use this"
                        authorNote="Spencer · one glance before each border"
                        heading="Three habits that shave a tankful off the trip"
                        intro="The table's below — but I only ever ask it three questions before I decide where the next fill-up happens."
                        columns={[
                            {
                                label: "01 · Cross-border",
                                title: "Fill before the dear country",
                                body: "Brim the tank in the cheaper neighbour. The Europe-wide spread is 50–60c/L — that's real money over a two-week loop.",
                            },
                            {
                                label: "02 · Where",
                                title: "Skip the motorway forecourt",
                                body: "Autoroute service stations run 10–20% over the country average. Come off at a junction and fill in town.",
                            },
                            {
                                label: "03 · Paying",
                                title: "Pump or cashier depends on the country",
                                body: "Some countries you pay at the pump before you fill; others you fill first and pay the cashier inside. Use a credit card, not debit — pay-at-pump stations put a lump-sum hold on the card and only settle the real amount later, which can tie up your cash for days on a debit card. And an unmanned pump often won't take a foreign card without a PIN.",
                            },
                        ]}
                        backstop="from the EU Commission Weekly Oil Bulletin, gov.uk and the U.S. EIA — converted from the live exchange-rate feed."
                    />
                    <div className="gt-guide-transition">
                        <div className="gt-eyebrow gt-guide-transition__eyebrow">
                            The numbers
                        </div>
                        <h2 className="gt-guide-transition__heading">
                            Pump prices, cheapest to dearest.
                        </h2>
                        <p className="gt-guide-transition__intro">
                            Average price per litre, all taxes in. Sorted so the
                            fill-up-here countries sit at the top.
                        </p>
                    </div>
                </>
            }
            articleSchema={{
                slug: META.slug,
                datePublished: META.datePublished,
                dateModified: META.dateModified,
                image: META.image?.src,
                description: META.summary,
            }}
        >
            <section className="gt-guide-feature gt-guide-feature--cream">
                <div className="gt-guide-feature__inner gt-guide-feature__inner--narrow">
                    {rows.length === 0 ? (
                        <div className="gt-card" style={{ padding: 16 }}>
                            <p className="gt-body-sm" style={{ margin: 0 }}>
                                Fuel price data isn't available right now. We
                                refresh every Monday — check back tomorrow.
                            </p>
                        </div>
                    ) : (
                        <div className="gt-fuel-table-card">
                            <Table
                                className="gt-fuel-table"
                                rowKey={(r: FuelRow) => r.countryCode}
                                columns={columns}
                                dataSource={sorted}
                                pagination={false}
                                size="middle"
                                scroll={{ x: "100%" }}
                            />
                        </div>
                    )}

                    {(weekOf || updatedAt) && (
                        <p className="gt-fuel-table__caption">
                            {weekOf && <>Week of {formatDate(weekOf)}</>}
                            {weekOf && updatedAt && " · "}
                            {updatedAt && <>Last refreshed {formatDate(updatedAt)}</>}
                        </p>
                    )}
                </div>
            </section>

            <GuideSection
                index={1}
                heading="How to read these numbers"
                intro="A country average is a starting point, not the price you'll pay at any given pump. Four things to keep in mind before you trust a single figure."
                highlight={{ glyph: "★", background: "var(--gt-accent)", color: "#3a2a0a" }}
                items={[
                    {
                        title: "These are pump prices, taxes in",
                        body: "The figure you actually pay at the forecourt — no VAT or duty to add on top.",
                    },
                    {
                        title: "Averages hide big local swings",
                        body: "Motorway service stations often charge 10–20% more than an out-of-town station in the same country.",
                    },
                    {
                        title: "LPG is EU-only here",
                        body: "The UK and US sources publish unleaded and diesel only, so the LPG column is blank for both.",
                    },
                    {
                        title: "Three official weekly sources",
                        body: "EU Commission Weekly Oil Bulletin, gov.uk Weekly Road Fuel Prices, and the U.S. EIA retail series — reused under their open-data licences.",
                    },
                ]}
            />

            <GuideCtaBand
                heading="We turn these numbers into a fuel budget."
                body="Each day's route knows which countries it crosses, so Planamoto estimates the fuel spend, rolls it into the trip budget, and splits it across the group in every currency. Clone a tour and see the whole cost before you commit."
            />

            <RelatedGuides
                slugs={[
                    "french-toll-roads",
                    "packing-europe",
                    "cycle-touring-packing",
                ]}
            />
        </GuideArticle>
    );
}

export default FuelPrices;
